import React, { useState } from "react";
import {
  Modal,
  ModalDialog,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from "@mui/joy";
import {
  X as Close,
  DownloadSimple as Download,
  Printer as Print,
  MagnifyingGlassPlus as ZoomIn,
  MagnifyingGlassMinus as ZoomOut,
} from "@phosphor-icons/react";
import "../../../lib/pdfWorkerSetup"; // Initialize PDF worker
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { fetchApiBlob } from "../../../lib/api";
import type { ContractField } from "../../../types";
import { PreviewFieldLayer } from "./PreviewFieldLayer";

interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  title?: string;
  onDownload?: () => void;
  onPrint?: () => void;
  /**
   * Placed contract fields to draw over the document. Without these the modal
   * shows the bare PDF, which is what made Preview look like it had thrown away
   * every field the designer had just placed.
   */
  fields?: ContractField[];
  /** Values already collected, keyed by field id, rendered in place. */
  fieldValues?: Record<string, any>;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  open,
  onClose,
  pdfUrl,
  title = "View Document",
  onDownload,
  onPrint,
  fields,
  fieldValues,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);
  const fieldCount = fields?.length ?? 0;

  // Calculate optimal scale to fit 80% of modal width
  React.useEffect(() => {
    const calculateOptimalScale = () => {
      // Modal is 95vw with max 1400px, so calculate effective width
      const viewportWidth = window.innerWidth;
      const modalWidth = Math.min(viewportWidth * 0.95, 1400);
      // Target 80% of modal width for PDF
      const targetWidth = modalWidth * 0.8;
      // Standard PDF width is 612px (8.5 inches at 72 DPI)
      const baseScale = targetWidth / 612;

      // Apply reasonable min/max limits
      const optimalScale = Math.min(Math.max(baseScale, 0.5), 3.0);
      setScale(optimalScale);
    };

    if (open) {
      calculateOptimalScale();
      window.addEventListener("resize", calculateOptimalScale);
      return () => window.removeEventListener("resize", calculateOptimalScale);
    }
  }, [open]);

  // Fetch PDF with authentication when modal opens
  React.useEffect(() => {
    // The cleanup closure can only see the blob URL from the render it was created
    // in, so track the live one in a ref - otherwise reopening the modal leaks the
    // previous object URL and can revoke the one react-pdf is still reading.
    const releaseBlobUrl = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    if (!open || !pdfUrl) {
      releaseBlobUrl();
      setPdfBlobUrl(null);
      return;
    }

    // A fetch started for an earlier pdfUrl must not overwrite a newer one
    let cancelled = false;

    const fetchPdfWithAuth = async () => {
      setLoading(true);
      setError(null);
      setNumPages(null);

      try {
        const blob = await fetchApiBlob(pdfUrl);
        if (cancelled) return;

        const blobUrl = URL.createObjectURL(blob);
        releaseBlobUrl();
        blobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch PDF:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load PDF document",
        );
        setLoading(false);
      }
    };

    fetchPdfWithAuth();

    return () => {
      cancelled = true;
    };
  }, [open, pdfUrl]);

  // Release the last object URL when the viewer goes away for good
  React.useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    [],
  );

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const handleDocumentLoadError = (error: Error) => {
    console.error("PDF document load error:", error);
    const errorMessage = error.message || "Unknown error";
    setError(`Failed to load PDF document: ${errorMessage}`);
    setLoading(false);
  };

  const handleSourceError = (error: Error) => {
    console.error("PDF source error:", error);
    const errorMessage = error.message || "Unknown error";
    setError(`Failed to load PDF source: ${errorMessage}`);
    setLoading(false);
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (pdfBlobUrl) {
      // Download the blob we already fetched with the Bearer token - pdfUrl itself
      // is an authenticated endpoint and would 401 when opened directly
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = title.replace(/[^a-z0-9]/gi, "_").toLowerCase() + ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setError("The document is still loading. Please try again in a moment.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ModalDialog
        variant="plain"
        sx={{
          width: "95vw",
          height: "95dvh",
          maxHeight: "95dvh",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          maxWidth: "1400px",
          p: 0,
          overflow: "hidden",
          bgcolor: "background.surface",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            p: 2,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Typography level="h4" noWrap>
              {title}
            </Typography>
            {fieldCount > 0 && (
              <Chip size="sm" variant="soft" color="primary">
                {fieldCount} field{fieldCount === 1 ? "" : "s"}
              </Chip>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {/* Zoom controls */}
            <IconButton
              variant="outlined"
              aria-label="Zoom out"
              onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
              disabled={scale <= 0.5}
            >
              <ZoomOut />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
              <Typography level="body-sm">
                {Math.round(scale * 100)}%
              </Typography>
            </Box>
            <IconButton
              variant="outlined"
              aria-label="Zoom in"
              onClick={() => setScale((s) => Math.min(3, s + 0.1))}
              disabled={scale >= 3}
            >
              <ZoomIn />
            </IconButton>

            {/* Action buttons */}
            <IconButton variant="outlined" onClick={handlePrint} title="Print">
              <Print />
            </IconButton>
            <IconButton
              variant="outlined"
              onClick={handleDownload}
              title="Download"
            >
              <Download />
            </IconButton>
            <IconButton variant="plain" color="neutral" onClick={onClose} aria-label="Close dialog">
              <Close />
            </IconButton>
          </Stack>
        </Box>

        {/* Native scrolling keeps the full page reachable at every zoom level. */}
        <Box
          role="region"
          aria-label="Document pages"
          tabIndex={0}
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overscrollBehavior: "contain",
            overflow: "auto",
            p: 2,
            bgcolor: "background.level2",
            // Improve scrollbar styling for better visibility
            "&::-webkit-scrollbar": {
              width: "12px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "var(--joy-palette-background-level1)",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--joy-palette-neutral-400)",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "var(--joy-palette-neutral-500)",
              },
            },
          }}
        >
          {error && (
            <Alert color="danger" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {/* Show loading state while fetching PDF */}
          {loading && !pdfBlobUrl && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Render Document component once we have the authenticated blob URL */}
          {open && pdfBlobUrl && (
            <Box sx={{ width: "max-content", minWidth: "100%" }}>
                <Document
                  file={pdfBlobUrl}
                  onLoadSuccess={handleDocumentLoadSuccess}
                  onLoadError={handleDocumentLoadError}
                  onSourceError={handleSourceError}
                  loading={
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  {numPages && numPages > 0 ? (
                    Array.from(new Array(numPages), (el, index) => {
                      // Placed fields carry a 0-indexed page, the way the
                      // designer writes them.
                      const pageFields = (fields || []).filter(
                        (field) => field.page === index,
                      );

                      return (
                        <Box
                          key={`page_${index + 1}`}
                          sx={{
                            mb: 2,
                          mx: "auto",
                            position: "relative",
                            width: "fit-content",
                          }}
                        >
                          {/* Page number indicator */}
                          {numPages > 1 && (
                            <Typography
                              level="body-xs"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "background.surface",
                                px: 1,
                                py: 0.5,
                                borderRadius: "sm",
                                boxShadow: "sm",
                                zIndex: 2,
                              }}
                            >
                              Page {index + 1} of {numPages}
                            </Typography>
                          )}
                          {/* The overlay is positioned against the rendered page
                              itself, so field coordinates (PDF units, top-left
                              origin) line up at any zoom. */}
                          <Box
                            sx={{
                              position: "relative",
                              lineHeight: 0,
                              display: "inline-block",
                            }}
                          >
                            <Page
                              pageNumber={index + 1}
                              scale={scale}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              onRenderSuccess={() => {}}
                              onRenderError={() => {}}
                            />
                            {pageFields.length > 0 && (
                              <PreviewFieldLayer
                                fields={pageFields}
                                fieldValues={fieldValues}
                                scale={scale}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Typography>No pages to display</Typography>
                    </Box>
                  )}
                </Document>
            </Box>
          )}
        </Box>

        {/* Footer with page info and scroll hint */}
        {numPages && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 1,
              flexShrink: 0,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.level1",
            }}
          >
            <Typography level="body-sm">
              {numPages} page{numPages > 1 ? "s" : ""}
            </Typography>
              <Typography level="body-sm" color="neutral">
              Scroll to view the full document
              </Typography>
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
};
